import { useCallback, useMemo, useState } from "react";
import { Alert, Spin, Typography } from "antd";
import { DragDropContext, type DropResult } from "@hello-pangea/dnd";
import { useTranslation } from "react-i18next";
import { useGo, useNotification } from "@refinedev/core";
import type {
  GridColumnMetadata,
  LifecycleInfo,
  LifecycleState,
} from "../../types";
import { KanbanColumn } from "./KanbanColumn";
import type { KanbanCardLabels } from "./KanbanCard";

const { Text } = Typography;

const COLUMN_COLORS = [
  "#1890ff",
  "#13c2c2",
  "#722ed1",
  "#faad14",
  "#52c41a",
  "#eb2f96",
  "#fa541c",
  "#2f54eb",
];

const UNCATEGORIZED_KEY = "__uncategorized__";
const UNCATEGORIZED_COLOR = "#8c8c8c";

const LOCALE_MAP: Record<string, string> = {
  vi: "vi-VN",
  en: "en-US",
};

interface KanbanBoardProps {
  data: Record<string, unknown>[];
  loading?: boolean;
  columns: GridColumnMetadata[];
  resource: string;
  lifecycleMap: LifecycleInfo | null;
  listLabelLookup: Record<string, Record<string, string>>;
  onItemMove: (itemId: string, targetState: string) => Promise<void>;
  isMoving?: boolean;
  /** Total items on server (to show warning if truncated) */
  total?: number;
}

/**
 * Check if a transition from one state to another is valid.
 * Converts state names to IDs and checks the transitions array.
 * Transitions in Aras store state GUIDs, not names.
 */
function isTransitionAllowed(
  lifecycleMap: LifecycleInfo,
  fromStateName: string,
  toStateName: string,
): boolean {
  const fromId = lifecycleMap.states.find((s) => s.name === fromStateName)?.id;
  const toId = lifecycleMap.states.find((s) => s.name === toStateName)?.id;
  if (!fromId || !toId) return false;
  return lifecycleMap.transitions.some(
    (t) => t.fromState === fromId && t.toState === toId,
  );
}

/**
 * Kanban board view that groups items by lifecycle state.
 * Supports drag-and-drop between states with transition validation.
 *
 * Hooks (useGo, useTranslation) are centralized here and passed
 * down as callbacks/labels to avoid per-card overhead.
 */
export function KanbanBoard({
  data,
  loading,
  columns,
  resource,
  lifecycleMap,
  listLabelLookup,
  onItemMove,
  isMoving,
  total,
}: KanbanBoardProps) {
  const { t, i18n } = useTranslation();
  const { open: notify } = useNotification();
  const go = useGo();

  // Track which specific item is being promoted
  const [promotingItemId, setPromotingItemId] = useState<string | null>(null);

  // Optimistic state: when set, overrides real data until API responds
  const [optimisticData, setOptimisticData] = useState<
    Record<string, unknown>[] | null
  >(null);
  const effectiveData = optimisticData ?? data;

  // Derive locale from current i18n language
  const locale = LOCALE_MAP[i18n.language] ?? i18n.language;

  // Lifecycle states sorted by sortOrder
  const sortedStates: LifecycleState[] = useMemo(() => {
    if (!lifecycleMap?.states) return [];
    return [...lifecycleMap.states].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [lifecycleMap?.states]);

  // Group items by state field
  const groupedItems = useMemo(() => {
    const groups: Record<string, Record<string, unknown>[]> = {};
    sortedStates.forEach((s) => {
      groups[s.name] = [];
    });
    groups[UNCATEGORIZED_KEY] = [];

    effectiveData.forEach((item) => {
      const value = item.state;
      const key = value ? String(value) : UNCATEGORIZED_KEY;
      if (groups[key]) {
        groups[key].push(item);
      } else {
        groups[UNCATEGORIZED_KEY].push(item);
      }
    });

    return groups;
  }, [effectiveData, sortedStates]);

  // Lifted navigation callbacks — stable refs via useCallback
  const handleView = useCallback(
    (id: string) => go({ to: `/${resource}/${id}` }),
    [go, resource],
  );
  const handleEdit = useCallback(
    (id: string) => go({ to: `/${resource}/${id}/edit` }),
    [go, resource],
  );

  // Pre-build card labels + shared strings once, avoid per-card useTranslation
  const { cardLabels, emptyText, uncategorizedLabel } = useMemo(
    () => ({
      cardLabels: {
        yes: t("common.yes", "Yes"),
        no: t("common.no", "No"),
        view: t("buttons.show", "View"),
        edit: t("buttons.edit", "Edit"),
        columnLabels: Object.fromEntries(
          columns.map((col) => [col.name, t(`table.${col.name}`, col.label)]),
        ),
      } as KanbanCardLabels,
      emptyText: t("kanban.noItems", "No items"),
      uncategorizedLabel: t("kanban.uncategorized", "Uncategorized"),
    }),
    [t, columns],
  );

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;

    // Prevent concurrent drags
    if (isMoving || promotingItemId) return;

    // Dropped outside
    if (!destination) return;

    // Same column — disable reorder to avoid visual snap-back
    if (destination.droppableId === source.droppableId) return;

    const fromState = source.droppableId;
    const toState = destination.droppableId;

    // Don't allow moving to/from uncategorized
    if (toState === UNCATEGORIZED_KEY || fromState === UNCATEGORIZED_KEY) {
      return;
    }

    // Validate transition
    if (lifecycleMap) {
      if (!isTransitionAllowed(lifecycleMap, fromState, toState)) {
        notify?.({
          type: "error",
          message: t("kanban.transitionNotAllowed", {
            from: fromState,
            to: toState,
          }),
        });
        return;
      }
    }

    // Track which card is being promoted
    setPromotingItemId(draggableId);

    // Optimistic update
    setOptimisticData((prev) => {
      const current = prev ?? data;
      return current.map((item) =>
        String(item.id) === draggableId ? { ...item, state: toState } : item,
      );
    });

    try {
      await onItemMove(draggableId, toState);
    } catch {
      // Revert handled by clearing optimistic state
    } finally {
      setOptimisticData(null);
      setPromotingItemId(null);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <Spin size="large" />
      </div>
    );
  }

  const isTruncated = total != null && total > effectiveData.length;

  return (
    <div>
      {isTruncated && (
        <Alert
          type="info"
          showIcon
          message={t("kanban.showingLimit", { count: effectiveData.length })}
          style={{ marginBottom: 12 }}
          closable
        />
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <div
          style={{
            display: "flex",
            gap: 12,
            overflowX: "auto",
            paddingBottom: 16,
            alignItems: "flex-start",
          }}
        >
          {/* Columns from lifecycle states */}
          {sortedStates.map((state, index) => {
            const colItems = groupedItems[state.name] || [];
            return (
              <KanbanColumn
                key={state.name}
                columnValue={state.name}
                columnLabel={state.label || state.name}
                items={colItems}
                columns={columns}
                groupByField="state"
                listLabelLookup={listLabelLookup}
                color={COLUMN_COLORS[index % COLUMN_COLORS.length]}
                emptyText={emptyText}
                locale={locale}
                promotingItemId={
                  colItems.some((i) => String(i.id) === promotingItemId)
                    ? promotingItemId
                    : null
                }
                onView={handleView}
                onEdit={handleEdit}
                cardLabels={cardLabels}
              />
            );
          })}

          {/* Uncategorized column (items without state) */}
          {groupedItems[UNCATEGORIZED_KEY]?.length > 0 && (
            <KanbanColumn
              columnValue={UNCATEGORIZED_KEY}
              columnLabel={uncategorizedLabel}
              items={groupedItems[UNCATEGORIZED_KEY]}
              columns={columns}
              groupByField="state"
              listLabelLookup={listLabelLookup}
              color={UNCATEGORIZED_COLOR}
              emptyText={emptyText}
              locale={locale}
              promotingItemId={null}
              onView={handleView}
              onEdit={handleEdit}
              cardLabels={cardLabels}
            />
          )}

          {/* Empty state */}
          {sortedStates.length === 0 && (
            <Text type="secondary">
              {t("kanban.notAvailable", "Kanban view is not available")}
            </Text>
          )}
        </div>
      </DragDropContext>
    </div>
  );
}

export default KanbanBoard;
